import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconUserStar,
  IconChevronRight,
  IconChevronLeft,
} from "@tabler/icons-react";
import MAvatar from "@/components/ui/atoms/MAvatar";
import { MButton } from "@/components/ui/atoms/MButtons";
import MBadge from "@/components/ui/atoms/MBadge";
import MDialog from "@/components/ui/atoms/MDialog";
import { getMemberInfo } from "@/hooks/useMembers";
import { useFamily } from "@/hooks/useFamily";
import { useFamilyStats } from "@/hooks/useFamilyStats";
import { useMemberAccess } from "@/hooks/useMemberAccess";
import MaatraLogo from "@/components/ui/atoms/MaatraLogo";
import MLoader from "@/components/ui/atoms/MLoader";
import { usePendingAccessRequests } from "@/hooks/usePendingAccessRequests";
import { setNavigationSource } from "@/utils/navigationSource";
import { useDispatch } from "react-redux";
import { setSelectedMember, clearSelectedMember } from "@/store/authSlice";
import styles from "./FamilyOverview.module.css";

export type OverviewType =
  | "documents"
  | "health-memories"
  | "reminders"
  | "emergency-cards"
  | "health-profiles"
  | "health-summary"
  | "pillbox"
  | "vitals"
  | "lab-trends";

interface OverviewConfig {
  title: string;
  description: string;
  getCount: (stats: any) => number;
  getStatus?: (stats: any) => {
    label: string;
    variant:
      | "green"
      | "yellow"
      | "red"
      | "orange"
      | "grey"
      | "purple-inverse"
      | "orange-alert"
      | "info";
  };
  actionType: "add" | "view" | "manage" | "navigate";
  actionLabel: string;
  showChevron?: boolean;
}

const overviewConfigs: Record<OverviewType, OverviewConfig> = {
  documents: {
    title: "Documents",
    description:
      "View health records added for each family member. These records help build a health history and support care over time.",
    getCount: (stats) => stats?.docsCount ?? 0,
    actionType: "add",
    actionLabel: "Add",
    showChevron: true,
  },
  "health-memories": {
    title: "Health memories",
    description:
      "View health memories added for each family member. These capture important past health events, especially when documents are missing.",
    getCount: (stats) => stats?.memoriesCount ?? 0,
    actionType: "add",
    actionLabel: "Add",
    showChevron: true,
  },
  reminders: {
    title: "Reminders",
    description:
      "View reminders you've set for your family. Only you receive these notifications, so you can stay on top of appointments and follow-ups.",
    getCount: (stats) => stats?.remindersCount ?? 0,
    actionType: "add",
    actionLabel: "Add",
    showChevron: true,
  },
  "emergency-cards": {
    title: "Emergency cards",
    description:
      "View emergency card readiness status for each family member. They include essential health details that can be shared quickly when needed.",
    getCount: () => 0,
    // "Complete" and "ready" are not distinguished in the UI — a card that
    // is usable in an emergency reads as ready either way, so both collapse
    // to the same label (and to the "View" CTA below).
    getStatus: (stats) => {
      const isReady =
        (stats?.emergencyCardComplete ?? false) ||
        (stats?.emergencyCardReady ?? false);
      return {
        label: isReady ? "Card is ready" : "Card is not ready",
        variant: isReady ? "green" : "yellow",
      };
    },
    actionType: "manage",
    actionLabel: "Manage",
    showChevron: false,
  },
  "health-profiles": {
    title: "Health profiles",
    description:
      "View health profile completion status for each family member. Health profiles help keep key health information organised in one place and are used in the emergency card.",
    getCount: () => 0,
    getStatus: (stats) => {
      const isComplete = stats?.healthProfileComplete ?? false;
      return {
        label: isComplete ? "Profile complete" : "Profile incomplete",
        variant: isComplete ? "green" : "yellow",
      };
    },
    actionType: "navigate",
    actionLabel: "",
    showChevron: true,
  },
  "health-summary": {
    title: "Health summaries",
    description:
      "View health summary readiness for each family member. Health summaries give a quick, doctor-ready snapshot of what matters most.",
    getCount: () => 0,
    getStatus: (stats) => {
      const ready = stats?.healthSummaryReady ?? false;
      return {
        label: ready ? "Summary ready" : "Summary not ready",
        variant: ready ? "green" : "yellow",
      };
    },
    actionType: "navigate",
    actionLabel: "",
    showChevron: true,
  },
  pillbox: {
    title: "Pillbox",
    description:
      "View Pillbox status for each family member. The Pillbox keeps their current, past and SOS medications in one place, along with a daily schedule.",
    getCount: () => 0,
    getStatus: (stats) => {
      const added = stats?.medicationsAdded ?? false;
      return {
        label: added ? "Added" : "Not added",
        variant: added ? "green" : "yellow",
      };
    },
    actionType: "navigate",
    actionLabel: "",
    showChevron: true,
  },
  vitals: {
    title: "Vitals",
    description:
      "Vitals tracker keeps readings for blood pressure, blood sugar, weight and height in one place, so changes are easy to spot.",
    getCount: () => 0,
    getStatus: (stats) => {
      const added = stats?.vitalsAdded ?? false;
      return {
        label: added ? "Added" : "Not added",
        variant: added ? "green" : "yellow",
      };
    },
    actionType: "navigate",
    actionLabel: "",
    showChevron: true,
  },
  "lab-trends": {
    title: "Lab trends",
    description:
      "View lab results for each family member. Lab trends brings results from different reports together and lets you pin the tests that matter most.",
    getCount: () => 0,
    getStatus: (stats) => {
      const ready = stats?.labInsightsReady ?? false;
      return {
        label: ready ? "Ready" : "Not ready",
        variant: ready ? "green" : "yellow",
      };
    },
    actionType: "navigate",
    actionLabel: "",
    showChevron: true,
  },
};

interface FamilyOverviewProps {
  type: OverviewType;
  onMemberAction?: (memberId: string, action: string) => void;
}

const FamilyOverview: React.FC<FamilyOverviewProps> = ({
  type,
  onMemberAction,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo(true);
  const loggedInMemberId = memberInfo.id;
  const {
    data: familyMembers = [],
    family,
    managedIds,
    managedLoading,
  } = useFamily();
  const {
    familyStats,
    fetchFamilyStats,
    loading: statsLoading,
  } = useFamilyStats({ memberInfo });
  const { hasAccess, handleRequestAccessWithCleanup, requestingAccess } =
    useMemberAccess();
  const [showNoAccessDialog, setShowNoAccessDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [selectedMemberForAccess, setSelectedMemberForAccess] =
    useState<any>(null);
  const { pendingRequests } = usePendingAccessRequests({
    familyMembers,
    managedIds,
    familyMemberType: memberInfo.family_member_type,
    enabled: true,
  });

  const config = overviewConfigs[type];

  React.useEffect(() => {
    if (memberInfo?.id && !familyStats) {
      fetchFamilyStats();
    }
  }, [memberInfo?.id, family?._id, memberInfo?.current_family, familyStats]);

  // Show loader while family members or stats are loading
  if (managedLoading || statsLoading || !familyStats) {
    return <MLoader />;
  }

  const canAccessMember = (memberId: string): boolean => {
    // Prevent access if there's a pending access request
    if (pendingRequests[memberId]) {
      return false;
    }

    // Check if FM has access to this member
    if (memberInfo.family_member_type === "fm") {
      const memberHasAccess = hasAccess(memberId, managedIds);
      if (!memberHasAccess) {
        // Show no access dialog
        const member = familyMembers.find((m: any) => m.id === memberId);
        setSelectedMemberForAccess(member);
        setShowNoAccessDialog(true);
        return false;
      }
    }

    return true;
  };

  const prepareMemberSelection = (memberId: string) => {
    // Set navigation source to current overview page
    setNavigationSource("overview", globalThis.window?.location.pathname);

    // Set selectedMember in Redux (which also updates localStorage)
    const member = familyMembers.find((m: any) => m.id === memberId);
    if (member) {
      if (memberId === loggedInMemberId) {
        dispatch(clearSelectedMember());
      } else {
        dispatch(setSelectedMember(member));
      }
    }
  };

  const handleMemberClick = (memberId: string) => {
    if (!canAccessMember(memberId)) {
      return;
    }

    prepareMemberSelection(memberId);

    if (config.showChevron && onMemberAction) {
      onMemberAction(memberId, "navigate");
    }
  };

  const handleAction = (memberId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!canAccessMember(memberId)) {
      return;
    }

    prepareMemberSelection(memberId);

    if (onMemberAction) {
      onMemberAction(memberId, config.actionType);
    }
  };

  const handleShowConsentDialog = () => {
    setShowNoAccessDialog(false);
    setShowConsentDialog(true);
  };

  const handleConfirmRequestAccess = async () => {
    if (!selectedMemberForAccess || !family) return;

    const memberId = memberInfo.id || (memberInfo as any)._id;
    const familyMemberId =
      selectedMemberForAccess.id || selectedMemberForAccess._id;
    const familyId = family._id || family.id;

    await handleRequestAccessWithCleanup(
      { memberId, familyId, familyMemberId },
      () => {
        setShowConsentDialog(false);
        setSelectedMemberForAccess(null);
      }
    );

    // Note: pending requests state is now managed by usePendingAccessRequests hook
    // The hook will automatically re-fetch when dependencies change
  };

  const getMemberStats = (memberId: string) => {
    return familyStats?.memberStats?.[memberId];
  };

  // "Ready" (green status, or a non-zero count for the count-only types)
  // members sort first, not-ready ones after — every config's getStatus
  // already resolves to "green" exactly when that member is ready/complete/
  // added, so this one check covers all of them without per-type cases.
  const isMemberReady = (member: any) => {
    const stats = getMemberStats(member.id);
    return config.getStatus
      ? config.getStatus(stats).variant === "green"
      : config.getCount(stats) > 0;
  };

  return (
    <div style={{ padding: "0 24px" }}>
      <div className={styles.gradientHeader}>
        <button
          type="button"
          className={styles.gradientBackBtn}
          onClick={() => navigate("/")}
          aria-label="Back"
        >
          <IconChevronLeft size={16} />
          <span>Back</span>
        </button>
        <h1 className={styles.gradientTitle}>{config.title}</h1>
      </div>

      {/* Main content area with white background */}
      <div
        style={{
          background: "#FFF",
          marginLeft: "-24px",
          marginRight: "-24px",
          padding: "0 24px",
          minHeight: "calc(100vh - 200px)",
          paddingBottom: "100px",
        }}
      >
        <div className={styles.descriptionCard}>
          <p className={styles.description}>{config.description}</p>
        </div>

        <div className={styles.membersList}>
          {[...familyMembers]
            .sort((a, b) => {
              // Ready members first, not-ready after.
              const aReady = isMemberReady(a);
              const bReady = isMemberReady(b);
              if (aReady !== bReady) return aReady ? -1 : 1;
              // Within the same readiness group: logged-in member first,
              // then alphabetical by first name.
              if (a.id === memberInfo.id) return -1;
              if (b.id === memberInfo.id) return 1;
              const aName = (a.first_name || "").toLowerCase();
              const bName = (b.first_name || "").toLowerCase();
              return aName.localeCompare(bName);
            })
            .map((member: any) => {
              const memberStats = getMemberStats(member.id);
              const count = config.getCount(memberStats);
              const status = config.getStatus?.(memberStats);
              const memberName = member.nickname || member.first_name;

              // Determine what to show based on type and count/status
              const isDocumentsType =
                type === "documents" ||
                type === "health-memories" ||
                type === "reminders";
              const isEmergencyCards = type === "emergency-cards";
              const isHealthProfiles = type === "health-profiles";
              const isHealthSummary = type === "health-summary";
              const isPillbox = type === "pillbox";
              const isVitals = type === "vitals";
              const isLabTrends = type === "lab-trends";
              // Chevron-only types: name + status badge, no count, no
              // add/manage button — just navigate on tap (or Ask for access).
              const isChevronOnlyType =
                isHealthProfiles ||
                isHealthSummary ||
                isPillbox ||
                isVitals ||
                isLabTrends;

              const shouldShowAddButton = isDocumentsType && count === 0;
              const shouldShowChevron = isDocumentsType && count > 0;

              // Emergency cards: View once the card is ready (see getStatus
              // above — "complete" and "ready" are treated the same), Manage
              // while it still needs setting up.
              const isCardComplete =
                (memberStats?.emergencyCardComplete ?? false) ||
                (memberStats?.emergencyCardReady ?? false);
              const emergencyCardButton = isCardComplete ? "View" : "Manage";
              const emergencyCardVariant = isCardComplete
                ? "filled"
                : "outline";

              const isOwner = member.family_member_type === "fo";
              const hasCheckmark = member.registered_member;

              return (
                <div key={member.id} className={styles.memberRow}>
                  {/* Avatar */}
                  <div className={styles.avatarWrapper}>
                    <MAvatar
                      size="md"
                      type={member.avatar ? "dp" : "no-dp"}
                      src={member.avatar}
                      text={`${member.first_name || ""}${
                        member.last_name ? " " + member.last_name : ""
                      }`.trim()}
                      badgeIcon={
                        isOwner ? <IconUserStar size={16} /> : undefined
                      }
                    />
                    {hasCheckmark && !isOwner && (
                      <div className={styles.checkmarkBadge}>
                        <MaatraLogo size={12} fill="#FBFBFB" />
                      </div>
                    )}
                  </div>

                  {/* Member name and status */}
                  {shouldShowChevron || isChevronOnlyType ? (
                    <button
                      className={styles.memberContent}
                      onClick={() => handleMemberClick(member.id)}
                      type="button"
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          alignItems: "flex-start",
                        }}
                      >
                        <span className={styles.memberName}>
                          {isEmergencyCards || isChevronOnlyType
                            ? memberName
                            : `${memberName} (${count})`}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {/* Show No Access badge for FM without access (except for emergency-cards and health-profiles) */}
                          {memberInfo.family_member_type === "fm" &&
                            !hasAccess(member.id, managedIds) &&
                            !isEmergencyCards &&
                            !isChevronOnlyType && (
                              <span className={styles.noAccessBadge}>
                                No Access
                              </span>
                            )}
                          {status && (
                            <MBadge variant={status.variant}>
                              {status.label}
                            </MBadge>
                          )}
                        </div>
                      </div>
                    </button>
                  ) : (
                    <div className={styles.memberContent}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          alignItems: "flex-start",
                        }}
                      >
                        <span className={styles.memberName}>
                          {isEmergencyCards || isChevronOnlyType
                            ? memberName
                            : `${memberName} (${count})`}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {/* Show No Access badge for FM without access (except for emergency-cards and health-profiles) */}
                          {memberInfo.family_member_type === "fm" &&
                            !hasAccess(member.id, managedIds) &&
                            !isEmergencyCards &&
                            !isChevronOnlyType && (
                              <span className={styles.noAccessBadge}>
                                No Access
                              </span>
                            )}
                          {status && (
                            <MBadge variant={status.variant}>
                              {status.label}
                            </MBadge>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action buttons / chevron */}
                  <div className={styles.memberActions}>
                    {/* Check if FM has access to this member */}
                    {memberInfo.family_member_type === "fm" &&
                    !hasAccess(member.id, managedIds) ? (
                      /* Show Ask for Access button for members without access */
                      <MButton
                        size="sm"
                        variant="outline"
                        disabled={pendingRequests[member.id]}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMemberForAccess(member);
                          setShowConsentDialog(true);
                        }}
                        style={{
                          color: "var(--colour-text-default, #2A2A2A)",
                          height: "34px",
                        }}
                      >
                        {pendingRequests[member.id]
                          ? "Access Requested"
                          : "Ask for access"}
                      </MButton>
                    ) : (
                      /* Show normal action buttons for members with access or FO */
                      <>
                        {/* Documents, Health Memories, Reminders */}
                        {shouldShowAddButton && (
                          <MButton
                            size="sm"
                            variant="filled"
                            style={{ height: "34px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(member.id, e);
                            }}
                          >
                            {config.actionLabel}
                          </MButton>
                        )}
                        {shouldShowChevron && (
                          <button
                            className={styles.chevronButton}
                            onClick={() => handleMemberClick(member.id)}
                          >
                            <IconChevronRight size={24} color="#2A2A2A" />
                          </button>
                        )}

                        {/* Emergency Cards */}
                        {isEmergencyCards && (
                          <MButton
                            size="sm"
                            variant={emergencyCardVariant}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Set selectedMember in Redux (which also updates localStorage)
                              const memberObj = familyMembers.find(
                                (m: any) => m.id === member.id
                              );
                              if (memberObj) {
                                if (member.id === loggedInMemberId) {
                                  dispatch(clearSelectedMember());
                                } else {
                                  dispatch(setSelectedMember(memberObj));
                                }
                              }
                              // Pass 'view' if card is complete, 'manage' if not
                              const action = isCardComplete ? "view" : "manage";
                              if (onMemberAction) {
                                onMemberAction(member.id, action);
                              }
                            }}
                            style={{
                              height: "34px",
                              color: isCardComplete
                                ? undefined
                                : "var(--colour-text-default, #2A2A2A)",
                            }}
                          >
                            {emergencyCardButton}
                          </MButton>
                        )}

                        {/* Health Profiles */}
                        {isChevronOnlyType && (
                          <button
                            className={styles.chevronButton}
                            onClick={() => handleMemberClick(member.id)}
                          >
                            <IconChevronRight size={24} color="#2A2A2A" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* No Access Dialog */}
      <MDialog
        isOpen={showNoAccessDialog}
        onClose={() => {
          setShowNoAccessDialog(false);
          setSelectedMemberForAccess(null);
        }}
        title="No profile access"
        description="You don't have access to this family member's profile. You can ask the Family Owner for access if you need it."
        primaryCtaText="Ask for access"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleShowConsentDialog}
        onSecondaryCta={() => {
          setShowNoAccessDialog(false);
          setSelectedMemberForAccess(null);
        }}
      />

      {/* Consent Confirmation Dialog */}
      <MDialog
        isOpen={showConsentDialog}
        onClose={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccess(null);
        }}
        title="Consent needed"
        description="Please confirm that you have this family member's consent to view their health information."
        primaryCtaText="Confirm"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleConfirmRequestAccess}
        onSecondaryCta={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccess(null);
        }}
        isPrimaryCtaLoading={requestingAccess}
      />
    </div>
  );
};

export default FamilyOverview;
